import React, { useEffect, useState } from "react";
import logo from "assets/img/logo.png";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "config";

import {
  Box,
  Flex,
  Heading,
  Text,
  Link,
  keyframes,
} from "@chakra-ui/react";
import { FaArrowRight, FaChevronDown, FaGooglePlay } from "react-icons/fa";

// â”€â”€â”€ Background keyframes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// â”€â”€â”€ Welcome animation keyframes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const gradientText = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const logoFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
`;

const lineReveal = keyframes`
  0% { width: 0; opacity: 0; }
  50% { opacity: 1; }
  100% { width: 60px; opacity: 1; }
`;

// Elastic pop entrance
const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.08); }
  70% { transform: scale(0.96); }
  100% { opacity: 1; transform: scale(1); }
`;

const slideUpBounce = keyframes`
  0% { opacity: 0; transform: translateY(50px); }
  60% { opacity: 1; transform: translateY(-6px); }
  80% { transform: translateY(3px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const slideUpSoft = keyframes`
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const buttonReveal = keyframes`
  0% { opacity: 0; transform: translateY(40px); }
  60% { opacity: 1; transform: translateY(-4px); }
  80% { transform: translateY(2px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const exitFade = keyframes`
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.97); }
`;

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function WelcomeScreen() {
  const { t, i18n } = useTranslation();

  // Animation phases â€” cinematic reveal
  // 0 = blank (seamless from Loading fade-out)
  // 1 = logo pops in â€” centered on screen, bigger
  // 2 = "Mufyn" title pops in below logo â€” still centered, bigger
  // 3 = hero group (logo+title) moves up & shrinks to final position
  // 4 = decorative line reveals
  // 5 = tagline slides up
  // 6 = CTA buttons appear
  // 7 = bottom "learn more" link fades in
  const [phase, setPhase] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [stats, setStats] = useState({ users: null, transactions: null, lastUpdated: null });
  const history = useHistory();

  // Fetch real public stats from API
  useEffect(() => {
    let active = true;
    axios
      .get(`${config.apiUrl}/public-stats`)
      .then((res) => { if (active) setStats(res.data); })
      .catch(() => { /* silently ignore — stats just won't show */ });
    return () => { active = false; };
  }, []);

  const handleCtaClick = (e) => {
    e.preventDefault();
    setIsExiting(true);
    setTimeout(() => {
      history.push("/auth/signup");
    }, 600);
  };

  const handleSigninClick = (e) => {
    e.preventDefault();
    setIsExiting(true);
    setTimeout(() => {
      history.push("/auth/signin");
    }, 600);
  };

  // Browser language detection
  useEffect(() => {
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split("-")[0];
      return ["en", "fr", "de", "es", "it"].includes(langCode) ? langCode : "en";
    };
    if (!i18n.language || i18n.language === "en") {
      i18n.changeLanguage(getBrowserLanguage());
    }
  }, [i18n]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    const timers = [
      setTimeout(() => setPhase(1), 400),    // Logo pops centered
      setTimeout(() => setPhase(2), 1000),   // Title pops centered
      setTimeout(() => setPhase(3), 2100),   // Move up + shrink
      setTimeout(() => setPhase(4), 2850),   // Line
      setTimeout(() => setPhase(5), 3100),   // Tagline
      setTimeout(() => setPhase(6), 3400),   // Buttons
      setTimeout(() => setPhase(7), 3750),   // Learn more
    ];

    return () => {
      document.body.style.overflow = "unset";
      timers.forEach(clearTimeout);
    };
  }, []);

  // Has the hero moved to final position?
  const heroSettled = phase >= 3;

  return (
    <Box
      minH="100vh"
      w="100%"
      position="relative"
      overflow="hidden"
      bg="#0a0d14"
    >
      {/* â”€â”€â”€ Background (identical to Loading screen) â”€â”€â”€ */}
      <Box position="absolute" top="0" left="0" right="0" bottom="0" zIndex="0">
        <Box
          position="absolute"
          top="0" left="0" right="0" bottom="0"
          bg="linear-gradient(to bottom right, #0a0d14, #0f1720, #0a0d14)"
        />
        <Box
          position="absolute"
          top="0" left="30%" w="40%" h="50%"
          bg="radial-gradient(ellipse at center, rgba(34, 197, 94, 0.15) 0%, transparent 70%)"
          filter="blur(40px)"
        />
        <Box
          position="absolute"
          bottom="0" right="20%" w="50%" h="50%"
          bg="radial-gradient(ellipse at center, rgba(16, 185, 129, 0.1) 0%, transparent 70%)"
          filter="blur(40px)"
        />
        <Box
          position="absolute" top="10%" left="5%"
          w="300px" h="300px" borderRadius="full"
          bg="radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)"
          filter="blur(40px)"
          animation={`${float} 8s ease-in-out infinite`}
        />
        <Box
          position="absolute" bottom="20%" right="10%"
          w="400px" h="400px" borderRadius="full"
          bg="radial-gradient(circle, rgba(74, 222, 128, 0.15) 0%, transparent 70%)"
          filter="blur(60px)"
          animation={`${float} 10s ease-in-out infinite reverse`}
        />
        <Box
          position="absolute" top="50%" left="50%"
          transform="translate(-50%, -50%)"
          w="600px" h="600px" borderRadius="full"
          bg="radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 60%)"
          filter="blur(80px)"
          animation={`${pulse} 6s ease-in-out infinite`}
        />
        <Box
          position="absolute" top="0" left="0" right="0" bottom="0"
          opacity="0.03"
          backgroundImage={`
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `}
          backgroundSize="80px 80px"
        />
        <Box
          position="absolute" top="30%" left="0" right="0" h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.3), transparent)"
          animation={`${shimmer} 3s linear infinite`}
          backgroundSize="200% 100%"
        />
        <Box
          position="absolute" top="70%" left="0" right="0" h="1px"
          background="linear-gradient(90deg, transparent, rgba(74, 222, 128, 0.2), transparent)"
          animation={`${shimmer} 4s linear infinite 1s`}
          backgroundSize="200% 100%"
        />
      </Box>

      {/* â”€â”€â”€ Main Content â”€â”€â”€ */}
      <Flex
        position="relative"
        zIndex="1"
        minH="100vh"
        direction="column"
        align="center"
        justify="flex-start"
        px={{ base: 6, md: 8 }}
        pt={{ base: "16vh", md: "12vh" }}        animation={isExiting ? `${exitFade} 0.6s ease-in-out forwards` : "none"}      >
        {/* â”€â”€ Hero Group (Logo + Title) â”€â”€
             Starts centered on screen & scaled up,
             then smoothly transitions to top position & normal scale */}
        <Flex
          direction="column"
          align="center"
          transform={heroSettled
            ? "translateY(0) scale(1)"
            : "translateY(28vh) scale(1.3)"
          }
          transition="transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)"
        >
          {/* Logo â€” pops in at phase 1 */}
          <Box
            position="relative"
            opacity={0}
            animation={phase >= 1
              ? `${popIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`
              : "none"
            }
            mb={1}
          >
            {/* Subtle glow */}
            <Box
              position="absolute"
              top="50%" left="50%"
              transform="translate(-50%, -50%)"
              w="120px" h="120px" borderRadius="full"
              bg="radial-gradient(circle, rgba(34, 197, 94, 0.35) 0%, rgba(34, 197, 94, 0.08) 50%, transparent 70%)"
              filter="blur(16px)"
              animation={heroSettled ? `${glowPulse} 4s ease-in-out infinite` : "none"}
              opacity={phase >= 2 ? 1 : 0}
              transition="opacity 0.8s ease"
            />
            {/* Float after settling */}
            <Box
              animation={heroSettled ? `${logoFloat} 4s ease-in-out infinite` : "none"}
            >
              <img
                src={logo}
                width="80px"
                alt="Mufyn"
                style={{
                  position: "relative",
                  zIndex: 1,
                  filter: "drop-shadow(0 0 20px rgba(34, 197, 94, 0.4))",
                }}
              />
            </Box>
          </Box>

          {/* Title â€” pops in at phase 2 */}
          <Box
            opacity={0}
            animation={phase >= 2
              ? `${popIn} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`
              : "none"
            }
          >
            <Heading
              color="white"
              fontSize={{ base: "44px", md: "56px" }}
              fontWeight="800"
              letterSpacing="-1px"
              bgGradient="linear(to-r, #fff, #4ade80, #22c55e, #4ade80, #fff)"
              bgClip="text"
              backgroundSize="300% auto"
              animation={`${gradientText} 6s linear infinite`}
              lineHeight="1.1"
            >
              Mufyn
            </Heading>
          </Box>
        </Flex>

        {/* â”€â”€ Below-hero content (appears after hero settles) â”€â”€ */}

        {/* Decorative line */}
        <Box
          h="2px"
          bg="linear-gradient(90deg, transparent, #22c55e, transparent)"
          borderRadius="full"
          my={3}
          animation={phase >= 4
            ? `${lineReveal} 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards`
            : "none"
          }
          opacity={0}
          w="0px"
        />

        {/* Tagline */}
        <Box
          maxW="400px"
          mb={6}
          opacity={0}
          animation={phase >= 5
            ? `${slideUpBounce} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`
            : "none"
          }
        >
          <Text
            color="gray.300"
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="400"
            lineHeight="1.6"
            textAlign="center"
          >
            {t("welcome.tagline")}
          </Text>
        </Box>

        {/* CTA Buttons */}
        <Flex
          direction="column"
          align="center"
          gap={5}
          w="100%"
          maxW="320px"
        >
          {/* Primary CTA — wow button with glow + shimmer */}
          <Box
            w="100%"
            opacity={0}
            animation={phase >= 6
              ? `${buttonReveal} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`
              : "none"
            }
          >
            <Box
              as="button"
              onClick={handleCtaClick}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={3}
              w="100%"
              h="60px"
              borderRadius="16px"
              border="none"
              bg="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
              color="white"
              fontWeight="700"
              fontSize="lg"
              letterSpacing="0.02em"
              cursor="pointer"
              position="relative"
              overflow="hidden"
              boxShadow="0 0 20px rgba(34, 197, 94, 0.3), 0 0 60px rgba(34, 197, 94, 0.1)"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{
                transform: "translateY(-3px) scale(1.02)",
                boxShadow: "0 12px 35px rgba(34, 197, 94, 0.5), 0 0 80px rgba(34, 197, 94, 0.2)",
              }}
              _active={{
                transform: "translateY(0) scale(0.98)",
              }}
            >
              <Text as="span" position="relative" zIndex={1} fontFamily="inherit">
                {t("welcome.cta_start")}
              </Text>
              <Box as={FaArrowRight} position="relative" zIndex={1} />  
            </Box>
          </Box>

          {/* Discreet public stats — just below the primary CTA */}
          {stats.users !== null && (
            <Text
              fontSize="11px"
              color="gray.600"
              textAlign="center"
              mt={-2}
              opacity={0}
              animation={phase >= 6
                ? `${slideUpSoft} 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.15s forwards`
                : "none"
              }
            >
              {Number(stats.users).toLocaleString(i18n.language === "fr" ? "fr-FR" : i18n.language)}{" "}
              {t("landing.stats.users_label")}
              {" · "}
              {Number(stats.transactions).toLocaleString(i18n.language === "fr" ? "fr-FR" : i18n.language)}{" "}
              {t("landing.stats.transactions_label")}
            </Text>
          )}

          {/* Secondary — text link, no background */}
          <Box
            opacity={0}
            animation={phase >= 6
              ? `${slideUpSoft} 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards`
              : "none"
            }
          >
            <Link
              as="button"
              onClick={handleSigninClick}
              color="gray.400"
              fontWeight="500"
              fontSize="sm"
              textDecoration="none"
              transition="all 0.3s ease"
              _hover={{
                color: "#4ade80",
                textDecoration: "none",
              }}
            >
              {t("welcome.cta_signin")}
            </Link>
          </Box>

          {/* Install suggestion — only in a mobile browser, not the native app */}
          {!window.AndroidBridge && (
            <Box
              opacity={0}
              animation={phase >= 6
                ? `${slideUpSoft} 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.35s forwards`
                : "none"
              }
            >
              <Link
                href={config.playStoreUrl}
                isExternal
                display="flex"
                alignItems="center"
                gap={2}
                px={4}
                py={2}
                borderRadius="full"
                border="1px solid rgba(34, 197, 94, 0.25)"
                bg="rgba(34, 197, 94, 0.06)"
                color="gray.300"
                fontSize="xs"
                fontWeight="600"
                transition="all 0.3s ease"
                _hover={{
                  textDecoration: "none",
                  bg: "rgba(34, 197, 94, 0.12)",
                  borderColor: "rgba(34, 197, 94, 0.45)",
                  color: "white",
                }}
              >
                <Box as={FaGooglePlay} color="#4ade80" fontSize="14px" />
                {t("welcome.install_app")}
              </Link>
            </Box>
          )}
        </Flex>

        {/* Discreet "Learn more" */}
        <Box
          position="absolute"
          bottom={{ base: "48px", md: "32px" }}
          left="50%"
          transform="translateX(-50%)"
          opacity={phase >= 7 ? 1 : 0}
          transition="opacity 0.8s ease"
        >
          <Link
            as={RouterLink}
            to="/auth/landing"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
            color="gray.600"
            fontSize="xs"
            fontWeight="400"
            letterSpacing="0.5px"
            textTransform="uppercase"
            _hover={{
              color: "gray.400",
              textDecoration: "none",
              "& svg": { transform: "translateY(2px)" },
            }}
            transition="color 0.3s ease"
          >
            {t("welcome.learn_more")}
            <Box as={FaChevronDown} size="10px" transition="transform 0.3s ease" />
          </Link>
        </Box>
      </Flex>
    </Box>
  );
}

export default WelcomeScreen;
